// Direct-to-Xano fetch helper. Uses the user's Xano authToken from /__app/me
// is NOT exposed to JS (it lives in the HttpOnly session cookie). Instead,
// requests go through Pages Functions at /api/* which proxy to Xano using the
// authToken from the session.
//
// For v0 we only have a tiny patient-search proxy — see functions/api/patients.js.
import { forceLogout } from './auth.js'

export function redirectToLogin() {
  forceLogout()
}

// Serialize params onto a URL. An ARRAY value is appended once per item
// (?tag=a&tag=b) rather than stringified — String(['a','b']) would yield the single
// value "a,b", which no proxy can split back apart. Empty items are dropped.
function applyParams(u, params) {
  if (!params) return
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue
    if (Array.isArray(v)) {
      for (const item of v) {
        if (item !== undefined && item !== null && item !== '') u.searchParams.append(k, String(item))
      }
      continue
    }
    u.searchParams.set(k, String(v))
  }
}

/* ══════════════════════════════════════════════════════════════════════════════
 * THE READ GATE — at most MAX_INFLIGHT GETs in the air at once, app-wide.
 *
 * 🚨 THIS EXISTS BECAUSE THE APP HAS BEEN TAKEN DOWN THREE TIMES BY REQUEST
 * BURSTS (Cloudflare Error 1102, 24/25/26 Aug 2026). Every one of them was a
 * page fanning out reads with `Promise.all` and no ceiling:
 *
 *   CallList#fetchEvidence   2 concurrent 30-day walks × per_page=500
 *   Reimbursement            16 payers × 6 pages × 500
 *   Treatments#fetchDay      ~11 branches, each up to 10 more pages in parallel
 *
 * All /api/* traffic lands on ONE Cloudflare Pages Worker. When a burst blows
 * that isolate's CPU ceiling, every request sharing it dies — including page
 * loads from users who never opened the offending screen. That is why these read
 * as "the site is down" rather than "one page is slow", and it is why the fix
 * belongs HERE and not in each page: the next bad fan-out will be written by
 * someone who has not read any of those incident notes.
 *
 * ⚠️ HTTP/2 IS WHY THIS IS NOT ALREADY HANDLED. Browsers cap ~6 connections per
 * origin on HTTP/1.1, which would have throttled these bursts for free. Over
 * HTTP/2 — which is what Cloudflare serves — the browser multiplexes hundreds of
 * streams down ONE connection, so `Promise.all` of 110 requests really does hit
 * the isolate as 110 concurrent requests. The old ceiling is gone; this restores
 * it in userspace.
 *
 * WRITES ARE DELIBERATELY NOT GATED. postJson/patchJson/apiDelete stay direct:
 * they are user-initiated, small, and latency-sensitive — queueing a save behind
 * six slow report reads makes the app feel broken, and no outage has ever come
 * from a write. Bulk writes were already solved server-side (Xano api 570 loops
 * over billing_ids in one call precisely so the browser stops fanning out).
 *
 * The gate is FIFO, so a page cannot starve behind a greedier one, and a queued
 * request that is aborted before it starts is dropped without ever being sent.
 * ═════════════════════════════════════════════════════════════════════════════ */
const MAX_INFLIGHT = 6

let _inflight = 0
const _waiting = []

function abortError() {
  // Callers test `e?.name === 'AbortError'` (see Outstanding.jsx), so a request
  // aborted while still QUEUED has to be indistinguishable from one aborted
  // mid-flight by fetch itself.
  try { return new DOMException('The operation was aborted.', 'AbortError') } catch { /* older engines */ }
  const e = new Error('The operation was aborted.')
  e.name = 'AbortError'
  return e
}

function acquire(signal) {
  if (signal?.aborted) return Promise.reject(abortError())
  if (_inflight < MAX_INFLIGHT) { _inflight += 1; return Promise.resolve() }
  return new Promise((resolve, reject) => {
    const entry = { resolve, reject, signal, onAbort: null }
    if (signal) {
      // Drop it from the queue rather than let it wake up later and fire a
      // request whose caller has already moved on — the exact waste the `signal`
      // parameter was added to prevent.
      entry.onAbort = () => {
        const i = _waiting.indexOf(entry)
        if (i >= 0) { _waiting.splice(i, 1); reject(abortError()) }
      }
      signal.addEventListener('abort', entry.onAbort, { once: true })
    }
    _waiting.push(entry)
    maybeWarn()
  })
}

// Hand the slot straight to the next waiter instead of decrementing and letting
// it re-acquire — that keeps `_inflight` honest and makes the queue strictly FIFO.
function release() {
  const next = _waiting.shift()
  if (!next) { _inflight -= 1; return }
  if (next.signal && next.onAbort) next.signal.removeEventListener('abort', next.onAbort)
  next.resolve()
}

/**
 * A deep queue is a BUG somewhere, not normal load — it means a page is fanning
 * out dozens of reads at once, which is what the three outages looked like from
 * the inside. Say so in the console instead of silently absorbing it, or the gate
 * turns a visible crash into an invisible slowdown and nobody ever fixes the page.
 * Throttled so a genuinely busy screen cannot spam the log.
 */
const WARN_QUEUE_DEPTH = 20
const WARN_EVERY_MS = 30000
let _lastWarn = 0
function maybeWarn() {
  if (_waiting.length < WARN_QUEUE_DEPTH) return
  const now = Date.now()
  if (now - _lastWarn < WARN_EVERY_MS) return
  _lastWarn = now
  console.warn(
    `[api] ${_waiting.length} reads queued behind ${MAX_INFLIGHT} in flight — ` +
    'a page is fanning out too many requests at once. See the read-gate note in src/lib/api.js.',
  )
}

/** Queue depth, for a debug overlay or a test. Not used by app code. */
export function apiReadStats() {
  return { inflight: _inflight, queued: _waiting.length, max: MAX_INFLIGHT }
}

// `signal` lets a caller cancel a request that is no longer wanted. Without it a
// superseded fetch keeps running server-side — on a heavy report that means every
// keystroke stacks another multi-second query on Xano and the newest one gets
// SLOWER, not faster. An abort surfaces as an AbortError; callers ignore it.
export async function apiGet(path, params, { signal } = {}) {
  const u = new URL(path, window.location.origin)
  applyParams(u, params)
  await acquire(signal)
  // ⚠️ The slot is held until the BODY is read, not just until headers arrive.
  // The Worker is still streaming (and still costing CPU) while `.json()` runs,
  // so releasing at `fetch()` would let 6 slots become 12 real concurrent
  // requests against the isolate — which is the thing being prevented.
  try {
    const r = await fetch(u.pathname + u.search, { credentials: 'include', signal })
    if (r.status === 401) {
      forceLogout()
      // 🚨 `finally` runs before this pending promise is adopted, so the slot IS
      // released. It has to be: on a dead session every in-flight read 401s at
      // once, and holding six slots against a promise that never settles would
      // deadlock the gate and freeze the app mid-navigation.
      return new Promise(() => {}) // suspend — navigation is in progress
    }
    if (!r.ok) {
      const text = await r.text().catch(() => '')
      const err = new Error(`HTTP ${r.status}: ${text || r.statusText}`)
      err.status = r.status
      throw err
    }
    return await r.json()
  } finally {
    release()
  }
}

// Memoized GET for static reference lists (doctors, branches, appointment types)
// so the booking calendar and the edit modal share ONE fetch each per session
// instead of re-requesting. Keyed by full path+query; a failed fetch is evicted
// so it can be retried.
const _getCache = new Map()
export function cachedGet(path, params) {
  const u = new URL(path, window.location.origin)
  applyParams(u, params)
  const key = u.pathname + u.search
  if (_getCache.has(key)) return _getCache.get(key)
  const p = apiGet(path, params).catch((e) => { _getCache.delete(key); throw e })
  _getCache.set(key, p)
  return p
}

// Drop memoized entries whose key starts with `prefix`, so the next cachedGet
// refetches. Needed once a page can WRITE to something another page reads from
// the cache — e.g. editing a doctor must not leave the booking calendar and the
// revenue report showing the pre-edit list for the rest of the session.
export function invalidateCache(prefix) {
  for (const key of [..._getCache.keys()]) {
    if (key.startsWith(prefix)) _getCache.delete(key)
  }
}

export async function postJson(path, body) {
  const r = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  })
  if (r.status === 401) {
    forceLogout()
    return new Promise(() => {})
  }
  if (!r.ok) {
    const text = await r.text().catch(() => '')
    const err = new Error(`HTTP ${r.status}: ${text || r.statusText}`)
    err.status = r.status
    // Some endpoints report partial success in the error body (e.g. a bulk write that
    // wrote some rows before failing). Hand it over parsed so callers can act on it.
    try { err.body = JSON.parse(text) } catch { err.body = null }
    throw err
  }
  return r.json().catch(() => ({}))
}

export async function apiDelete(path) {
  const r = await fetch(path, { method: 'DELETE', credentials: 'include' })
  if (r.status === 401) {
    forceLogout()
    return new Promise(() => {})
  }
  if (!r.ok) {
    const text = await r.text().catch(() => '')
    const err = new Error(`HTTP ${r.status}: ${text || r.statusText}`)
    err.status = r.status
    // Parsed like postJson's: a refused delete carries a Thai `detail` that is the
    // whole point of the refusal ("หักยอดทันตแพทย์แล้ว — ลบงาน Lab นี้ไม่ได้"), and
    // showing it beats showing "HTTP 409".
    try { err.body = JSON.parse(text) } catch { err.body = null }
    throw err
  }
  return r.json().catch(() => ({}))
}

export async function patchJson(path, body) {
  const r = await fetch(path, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  })
  if (r.status === 401) {
    forceLogout()
    return new Promise(() => {}) // suspend — navigation is in progress
  }
  if (!r.ok) {
    const text = await r.text().catch(() => '')
    const err = new Error(`HTTP ${r.status}: ${text || r.statusText}`)
    err.status = r.status
    // Parsed like postJson's. A 409 from the lost-update gate carries the FIELDS
    // that moved, and the drawer shows them — reading that back out of the
    // message string is what apiErrorText already has to do for the text.
    try { err.body = JSON.parse(text) } catch { err.body = null }
    throw err
  }
  return r.json().catch(() => ({}))
}
